/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useState, useEffect } from "react";

import useStateRef from "react-usestateref";

import { FetchError, FetchSuccess } from "lib/managers";
import sleep from "utils/sleep";

import useComponentDidMount from "./useComponentDidMount";

// prettier-ignore
type AcceptableState = string | number | boolean | Object | any[] | null | undefined

type UseFetcherOptions<T, R> = Partial<{
  defaultState: R | null;
  startOnMount: boolean;
  startAfter: number;
  /* key is important for caching, no key means no caching. */
  key: string | null;
  hasCache: boolean;
  shouldResetData: () => boolean;
  onSuccess: (data: R | null) => void;
  onError: (error: any) => void;
  onDataChange: (data: R | null, changeTimes: number) => void;
  triggerLoadingOnMutate: boolean;
  shouldRefetchOnKeyChange: boolean;
  shapeData: (data: T | null) => R | null;
  /* if false, the fetcher will not be called. */
  enabled: boolean;
  retries: number;
  retryDelay: number;
  refetchOnConditions: Array<boolean | string | number | (() => boolean)>;
  interval: number | null;
}>;

const useFetcher = <T extends AcceptableState, R extends T = T>(
  fetcher: () => Promise<FetchSuccess<T> | FetchError>,
  opts?: UseFetcherOptions<T, R>,
) => {
  const settings: Required<UseFetcherOptions<T, R>> = {
    defaultState: null,
    startOnMount: true,
    startAfter: 0,
    key: null,
    hasCache: true,
    shouldResetData: () => false,
    onSuccess: () => { },
    onError: () => { },
    onDataChange: () => { },
    triggerLoadingOnMutate: true,
    shouldRefetchOnKeyChange: true,
    shapeData: (data) => data as unknown as R,
    enabled: true,
    retries: 0,
    retryDelay: 1000,
    refetchOnConditions: [],
    interval: null,
  };

  if (opts) {
    Object.assign(settings, opts);
    settings.startOnMount = settings.enabled ? settings.startOnMount : false; // if enabled is false, startOnMount should be false.
  }

  const [cache, setCache] = useState<Record<string, R | null>>({});
  const [changeTimes, setChangeTimes] = useState(0);

  const [state, setState, stateRef] = useStateRef({
    data: settings.defaultState,
    isError: false,
    isSuccess: false,
    isLoading: settings.startOnMount,
    hasFetched: false,
    error: null as unknown,
    mutate: (async () => { }) as typeof mutateFn,
    refetch: async () => { },
  });

  const mutateFn = async (
    payload: T | ((data: T | null) => R | null) | null,
    newKey?: string,
  ) => {
    let _key = newKey || settings.key;

    if (settings.triggerLoadingOnMutate) triggerLoadingOnMutate();

    const data =
      typeof payload === "function" ? payload(stateRef.current.data) : payload;

    //@ts-ignore
    if (_key) setCache((prev) => ({ ...prev, [_key]: data }));
    setState((prev) => ({ ...prev, data, isLoading: false }));
  };

  const fetchData = async () => {
    if (settings.key && cache.hasOwnProperty(settings.key)) {
      if (settings.triggerLoadingOnMutate) triggerLoadingOnMutate();

      settings.onSuccess(cache[settings.key]);
      onDataChange(cache[settings.key]);

      return setState({
        data: cache[settings.key],
        isError: false,
        isSuccess: true,
        isLoading: false,
        hasFetched: true,
        error: null,
        mutate: mutateFn,
        refetch: fetchData,
      });
    }

    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await fetcher();

      if (response.ok === false) {
        if (response instanceof FetchError) {
          throw response.response;
        } else {
          throw response;
        }
      }

      let data: R | null;
      if (response instanceof FetchSuccess) {
        // if T will transform to R, shapeData will take care of it below
        // otherwise T = R anyways
        data = response.data as R;
      } else {
        // not a FetchError but a raw Response that succeeded without
        // being passed to new FetchSuccess
        data = await (response as unknown as Response).json();
      }

      if (settings.shapeData) data = settings.shapeData(data);

      if (settings.key && settings.hasCache) {
        setCache((prev) => ({ ...prev, [settings.key!]: data }));
      }

      setState((prev) => ({
        ...prev,
        data,
        isSuccess: true,
        isError: false,
        mutate: mutateFn,
        hasFetched: true,
        refetch: fetchData,
      }));

      settings.onSuccess(data);
      onDataChange(data);

      let newState;
      setState((prev) => {
        newState = { ...prev, isLoading: false };
        return newState;
      });

      return newState;
    } catch (error) {
      settings.onError(error);

      if (settings.retries > 0) {
        await sleep(settings.retryDelay);
        settings.retries--;
        fetchData();
      } else {
        setState((prev) => ({ ...prev, isError: true, error }));
      }
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
      settings.startAfter = 0;
    }
  };

  const triggerLoadingOnMutate = async () => {
    setState((prev) => ({ ...prev, isLoading: true }));
    await sleep(1000);
  };

  const onDataChange = (data: R | null) => {
    const _changeTimes = changeTimes + 1;
    setChangeTimes(_changeTimes);
    settings.onDataChange(data, _changeTimes);
  };

  useComponentDidMount(() => {
    let canContinue = true;

    settings.refetchOnConditions.forEach((condition) => {
      if (typeof condition === "function") {
        canContinue = condition();
      } else {
        canContinue = !!condition;
      }
    });

    if (!canContinue) return;

    if (settings.shouldResetData()) {
      return setState((prev) => ({
        ...prev,
        data: settings.defaultState,
      }));
    }

    if (!settings.enabled) return;
    fetchData();
  }, [settings.key, ...settings.refetchOnConditions]);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      mutate: mutateFn,
      refetch: fetchData,
    }));

    if (settings.interval && settings.enabled) {
      const t = setInterval(fetchData, settings.interval);
      return () => clearInterval(t);
    }

    if (!settings.startOnMount || !settings.enabled) return;
    const t = setTimeout(fetchData, settings.startAfter || 0);
    return () => clearTimeout(t);
  }, []);

  return { ...state, hashKeys: Object.keys(cache), cache };
};

export default useFetcher;
